'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { authService } from '../lib/authService';
import { navigationService } from '../lib/navigationService';
import { offlineManager } from '../lib/offlineManager';
import { permissionCache } from '../lib/permissionCache';
import { requiresPersistentSession, isPublicPage } from '../lib/platformUtils';
import { wsClient } from '../lib/websocket/TotalAccessWebSocket';
import { clearDashboardShellSnapshot } from '../lib/offline/dashboardShellStore';
import { isPortalSessionLocked, setPortalSessionLocked, PORTAL_EXTERNAL_ACTIVITY_EVENT } from '../lib/sessionLockState';

interface SessionConfig {
  timeoutMinutes: number;
  warningSeconds: number;
  checkIntervalSeconds: number;
}

interface SessionState {
  isActive: boolean;
  lastActivity: number;
  timeUntilTimeout: number;
  showWarning: boolean;
  warningCountdown: number;
  isLocked: boolean;
}

const DEFAULT_CONFIG: SessionConfig = {
  timeoutMinutes: 60,
  warningSeconds: 30,
  checkIntervalSeconds: 60,
};

function getPersistedLockState(): boolean {
  return isPortalSessionLocked();
}

export function useSessionManager(config: Partial<SessionConfig> = {}) {
  const router = useRouter();
  const pathname = usePathname();
  // Use individual primitive values as deps so finalConfig is stable when
  // config is the default {} (new object each render). Without this, every
  // render creates a new finalConfig reference, causing effects that depend
  // on it (like the portalSessionManager lock-callback bridge) to tear down
  // and re-register on every render.
  const finalConfig = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config.timeoutMinutes, config.warningSeconds, config.checkIntervalSeconds]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally use primitive deps so finalConfig is stable when config is the default {}

  const isPersistent = useMemo(() => requiresPersistentSession(), []);

  const [sessionState, setSessionState] = useState<SessionState>({
    isActive: isPersistent ? true : false,
    lastActivity: Date.now(),
    timeUntilTimeout: finalConfig.timeoutMinutes * 60 * 1000,
    showWarning: false,
    warningCountdown: finalConfig.warningSeconds,
    isLocked: false,
  });

  // CRITICAL: shouldMonitorSession must be evaluated AFTER hydration, not during SSR.
  // Previously this was a useMemo with stable deps — both stable
  // values that never change. During SSR, `typeof window === 'undefined'` made the
  // memo return false. During client hydration, the deps were identical to the server
  // values, so React reused the server-computed false — FOREVER. This permanently
  // disabled session monitoring, lock callbacks, and inactivity timers, causing the
  // PortalLockScreen to never appear on automatic timeout (while manual lock from
  // Header still worked because it bypasses shouldMonitorSession).
  //
  // Using useState + useEffect ensures the value is computed on the client after
  // hydration, and re-evaluated when auth state or route changes.
  const [shouldMonitorSession, setShouldMonitorSession] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isPersistent) {
      console.log('[useSessionManager] 📱 Persistent platform (mobile/desktop/PWA) - skipping inactivity monitoring');
      setShouldMonitorSession(false);
      return;
    }

    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.startsWith('/auth/');
    const isAuthenticated = authService.isAuthenticated();
    const shouldMonitor = isAuthenticated && !isAuthPage && !isPublicPage(currentPath);

    setShouldMonitorSession(shouldMonitor);

    // Re-evaluate when auth state changes (login, logout, token expiry)
    const onAuthChange = () => {
      const path = window.location.pathname;
      const authed = authService.isAuthenticated();
      if (!authed) {
        // Central reset: any auth loss (logout, session_terminated push,
        // clearAuth) clears lock state so a re-login on the same JS runtime
        // doesn't immediately re-show the lock screen.
        setPortalSessionLocked(false);
        setSessionState(prev => prev.isLocked || prev.showWarning
          ? { ...prev, isLocked: false, showWarning: false }
          : prev);
      }
      setShouldMonitorSession(
        authed && !path.startsWith('/auth/') && !isPublicPage(path) && !isPersistent
      );
    };
    authService.addEventListener('authStateChanged', onAuthChange);
    return () => authService.removeEventListener('authStateChanged', onAuthChange);
  }, [isPersistent, pathname]);

  useEffect(() => {
    const persisted = getPersistedLockState();
    if (!persisted) {
      return;
    }

    if (authService.isAuthenticated()) {
      setSessionState(prev => ({ ...prev, isLocked: true }));
      return;
    }

    console.log('[useSessionManager] Lock state found but no valid session — clearing lock');
    setPortalSessionLocked(false);
  }, []);

  // Bridge portalSessionManager lock/unlock callbacks to React state.
  // portalSessionManager has its own activity tracking (DOM listeners + 10s
  // interval) that calls lockPortal() after 1 hour of inactivity. Without this
  // bridge, the lock is detected internally but never reflected in the UI
  // because PortalLockScreen visibility is driven by sessionState.isLocked.
  useEffect(() => {
    if (!shouldMonitorSession) return;

    let cancelled = false;

    import('../lib/portalSessionManager').then(({ portalSessionManager }) => {
      if (cancelled) return;

      // Sync current lock state on registration — the portal may have locked
      // while this session was on a public/auth route where monitoring (and
      // therefore this callback) was disabled.
      if (portalSessionManager.isLocked) {
        setPortalSessionLocked(true);
        setSessionState(prev => ({ ...prev, isLocked: true, showWarning: false }));
      }

      portalSessionManager.registerLockCallback((locked: boolean) => {
        if (locked) {
          console.log('[useSessionManager] Lock callback from portalSessionManager — locking UI');
          setPortalSessionLocked(true);
          setSessionState(prev => ({
            ...prev,
            isLocked: true,
            showWarning: false,
          }));
        } else {
          console.log('[useSessionManager] Unlock callback from portalSessionManager — unlocking UI');
          setPortalSessionLocked(false);
          setSessionState(prev => ({
            ...prev,
            isLocked: false,
            lastActivity: Date.now(),
            timeUntilTimeout: finalConfig.timeoutMinutes * 60 * 1000,
            showWarning: false,
            warningCountdown: finalConfig.warningSeconds,
          }));
        }
      });
    }).catch(() => {});

    return () => {
      cancelled = true;
      import('../lib/portalSessionManager').then(({ portalSessionManager }) => {
        portalSessionManager.registerLockCallback(() => {});
      }).catch(() => {});
    };
  }, [shouldMonitorSession, finalConfig]);

  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTimeout = useCallback(async () => {
    console.log('[useSessionManager] Session inactivity timeout - locking portal');
    setPortalSessionLocked(true);
    setSessionState(prev => ({
      ...prev,
      isLocked: true,
      showWarning: false,
    }));

    try {
      const { portalSessionManager } = await import('../lib/portalSessionManager');
      portalSessionManager.lockPortal?.();
    } catch {
      // Non-critical
    }
  }, []);

  const showTimeoutWarning = useCallback(() => {
    setSessionState(prev => ({
      ...prev,
      showWarning: true,
      warningCountdown: finalConfig.warningSeconds,
    }));

    let countdown = finalConfig.warningSeconds;
    const countdownInterval = setInterval(() => {
      countdown -= 1;
      setSessionState(prev => ({
        ...prev,
        warningCountdown: countdown,
      }));

      if (countdown <= 0) {
        clearInterval(countdownInterval);
        void handleTimeout();
      }
    }, 1000);

    logoutTimeoutRef.current = setTimeout(() => {
      clearInterval(countdownInterval);
      void handleTimeout();
    }, finalConfig.warningSeconds * 1000);
  }, [finalConfig, handleTimeout]);

  const updateActivity = useCallback(() => {
    const now = Date.now();

    if (now - sessionState.lastActivity < 5000) {
      return;
    }

    // If the portal is already locked (e.g. portalSessionManager locked it
    // on first interaction after a long absence), don't reset the activity
    // timer — the lock screen should stay visible until the user unlocks.
    if (sessionState.isLocked) {
      return;
    }

    // Notify the WebSocket inactivity enforcer of real user activity.
    // This is best-effort: if the socket is not connected, the local
    // timer still drives the frontend lock; the server enforcer is the
    // authoritative backstop that works across tabs.
    wsClient.sendActivity();

    setSessionState(prev => ({
      ...prev,
      lastActivity: now,
      timeUntilTimeout: finalConfig.timeoutMinutes * 60 * 1000,
      showWarning: false,
      warningCountdown: finalConfig.warningSeconds,
    }));

    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }

    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, (finalConfig.timeoutMinutes * 60 * 1000) - (finalConfig.warningSeconds * 1000));
  }, [finalConfig, showTimeoutWarning, sessionState.lastActivity, sessionState.isLocked]);

  const extendSession = useCallback(() => {
    updateActivity();
  }, [updateActivity]);

  const flushOfflinePermissions = useCallback(async () => {
    try {
      const activeUser = authService.getUser();
      const clientId = Number(
        activeUser?.client_id
          || (typeof window !== 'undefined' ? window.localStorage.getItem('current_client_id') : 0)
          || 0
      );
      const userId = Number(activeUser?.id || 0);

      permissionCache.clearCache();
      navigationService.clearCache();
      offlineManager.clearPermissionData();
      await offlineManager.clearNavigationData();

      if (clientId && userId) {
        await clearDashboardShellSnapshot(clientId, userId);
      }

      if (typeof window !== 'undefined') {
        localStorage.removeItem('dashboard_cached_stats');
      }

      console.log('[useSessionManager] Flushed offline navigation and permission snapshots');
    } catch (error) {
      console.error('[useSessionManager] Failed to flush offline permissions:', error);
    }
  }, []);

  const lockSession = useCallback(() => {
    console.log('[useSessionManager] Manual lock triggered');
    setPortalSessionLocked(true);
    setSessionState(prev => ({
      ...prev,
      isLocked: true,
      showWarning: false,
    }));

    import('../lib/portalSessionManager').then(({ portalSessionManager }) => {
      if (portalSessionManager.lockPortal) {
        portalSessionManager.lockPortal();
      }
    }).catch(() => {});
  }, []);

  const unlockSession = useCallback(async () => {
    console.log('[useSessionManager] Session unlock requested');

    let portalSessionManager: Awaited<typeof import('../lib/portalSessionManager')>['portalSessionManager'] | null = null;

    try {
      const portalSessionModule = await import('../lib/portalSessionManager');
      portalSessionManager = portalSessionModule.portalSessionManager;

      const sessionStillValid = await portalSessionManager.forceHeartbeat();
      if (!sessionStillValid) {
        console.warn('[useSessionManager] Unlock heartbeat could not confirm the session; proceeding only if local auth is still present');
        if (!authService.isAuthenticated()) {
          return;
        }
      }
    } catch (error) {
      console.warn('[useSessionManager] Unlock heartbeat failed; proceeding with local unlock state', error);
      if (!authService.isAuthenticated()) {
        return;
      }
    }

    console.log('[useSessionManager] Session unlocked');
    setPortalSessionLocked(false);
    setSessionState(prev => ({
      ...prev,
      isLocked: false,
      lastActivity: Date.now(),
      timeUntilTimeout: finalConfig.timeoutMinutes * 60 * 1000,
      showWarning: false,
      warningCountdown: finalConfig.warningSeconds,
    }));

    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, (finalConfig.timeoutMinutes * 60 * 1000) - (finalConfig.warningSeconds * 1000));

    portalSessionManager?.unlockPortal();
  }, [finalConfig, showTimeoutWarning]);

  const logout = useCallback(async () => {
    // Clear lock state so a subsequent login on the same JS runtime (soft
    // navigation keeps the singleton alive) doesn't immediately re-show the
    // lock screen.
    setPortalSessionLocked(false);
    // reason=signed_out tells the login page this was an explicit user
    // sign-out — on TCA-managed devices the auto-login hook must not
    // silently sign the user straight back in.
    try {
      await authService.logout();
      setSessionState(prev => ({
        ...prev,
        isActive: false,
        isLocked: false,
        showWarning: false,
      }));
      router.push('/auth/login?reason=signed_out');
    } catch (error) {
      console.error('Error during manual logout:', error);
      setSessionState(prev => ({
        ...prev,
        isActive: false,
        isLocked: false,
        showWarning: false,
      }));
      router.push('/auth/login?reason=signed_out');
    }
  }, [router]);

  const handleSessionExpired = useCallback(async () => {
    console.log('[useSessionManager] Session expired — clearing lock and redirecting to login');
    setPortalSessionLocked(false);
    setSessionState(prev => ({
      ...prev,
      isActive: false,
      isLocked: false,
      showWarning: false,
    }));
    try {
      await authService.logout('session_timeout');
    } catch {
      // Force navigation even if logout API fails
    }
    router.push('/auth/login?reason=session_timeout');
  }, [router]);

  const checkSessionValidity = useCallback(async () => {
    if (!shouldMonitorSession) {
      return;
    }

    try {
      if (!authService.isAuthenticated()) {
        void handleSessionExpired();
      }
    } catch (error) {
      console.error('Error checking session validity:', error);
    }
  }, [handleSessionExpired, shouldMonitorSession]);

  useEffect(() => {
    if (!shouldMonitorSession) {
      return;
    }

    // `wheel` replaces `scroll` — see portalSessionManager.ts for full rationale.
    // `scroll` events fire on programmatic scrollTo() and layout-shift-induced
    // scroll adjustments (background sync, WebSocket, token refresh, HMR),
    // which falsely reset the inactivity timer. `wheel` only fires on genuine
    // user input (mouse wheel / trackpad). Touch scrolling is covered by
    // `touchstart`; keyboard scrolling by `keydown`.
    // NOTE: `click` is deliberately excluded — programmatic element.click()
    // calls (file pickers, hidden-input triggers, auto-submit) dispatch
    // untrusted click events that would falsely reset the inactivity timer.
    const events = ['mousedown', 'keydown', 'wheel', 'touchstart'];

    const handleActivity = () => {
      if (sessionState.isActive) {
        updateActivity();
      }
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // External activity (TCA OS events, stay_alive session-control commands)
    // resets the inactivity timer through the same path as real DOM input —
    // updateActivity also forwards the WS `activity` message to the server
    // enforcer so presence stays fresh.
    window.addEventListener(PORTAL_EXTERNAL_ACTIVITY_EVENT, handleActivity);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener(PORTAL_EXTERNAL_ACTIVITY_EVENT, handleActivity);
    };
  }, [sessionState.isActive, updateActivity, shouldMonitorSession]);

  useEffect(() => {
    if (!shouldMonitorSession || !sessionState.isActive) {
      return;
    }

    checkIntervalRef.current = setInterval(() => {
      if (!sessionState.isActive) return;

      const now = Date.now();
      const timeSinceActivity = now - sessionState.lastActivity;
      const timeUntilTimeout = Math.max(0, (finalConfig.timeoutMinutes * 60 * 1000) - timeSinceActivity);

      setSessionState(prev => ({
        ...prev,
        timeUntilTimeout,
      }));

      if (timeSinceActivity > 60000) {
        void checkSessionValidity();
      }
    }, finalConfig.checkIntervalSeconds * 1000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [sessionState.isActive, sessionState.lastActivity, finalConfig, checkSessionValidity, shouldMonitorSession]);

  // Schedule the initial inactivity timeout when monitoring starts.
  // Without this, activityTimeoutRef is never set until the first user
  // interaction, so a user who loads the page and walks away is never locked.
  useEffect(() => {
    if (!shouldMonitorSession || !sessionState.isActive) return;
    if (activityTimeoutRef.current) return; // Already scheduled (e.g. by updateActivity)

    activityTimeoutRef.current = setTimeout(() => {
      showTimeoutWarning();
    }, (finalConfig.timeoutMinutes * 60 * 1000) - (finalConfig.warningSeconds * 1000));
    // NOTE: No cleanup — activityTimeoutRef is managed by updateActivity and
    // the unmount cleanup effect below.
  }, [shouldMonitorSession, sessionState.isActive, finalConfig, showTimeoutWarning]);

  useEffect(() => {
    setSessionState(prev => ({
      ...prev,
      isActive: isPersistent ? true : shouldMonitorSession,
    }));
  }, [shouldMonitorSession, isPersistent]);

  // Sync lock state from portalSessionManager when tab becomes visible.
  // When a browser tab is backgrounded for hours, Chrome freezes timers and
  // React may not re-render even if portalSessionManager locked the portal.
  // This handler ensures the lock screen shows immediately on tab resume.
  useEffect(() => {
    if (!shouldMonitorSession) return;
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      import('../lib/portalSessionManager').then(({ portalSessionManager }) => {
        if (portalSessionManager.isLocked && !sessionState.isLocked) {
          console.log('[useSessionManager] Tab resumed — syncing locked state from portalSessionManager');
          setPortalSessionLocked(true);
          setSessionState(prev => ({
            ...prev,
            isLocked: true,
            showWarning: false,
          }));
        }
      }).catch(() => {});
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [shouldMonitorSession, sessionState.isLocked]);

  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (logoutTimeoutRef.current) {
        clearTimeout(logoutTimeoutRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!sessionState.isLocked) return;

    const checkAuth = () => {
      if (!authService.isAuthenticated()) {
        console.log('[useSessionManager] Locked but session expired — redirecting to login');
        void handleSessionExpired();
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 30000);
    return () => clearInterval(interval);
  }, [sessionState.isLocked, handleSessionExpired]);

  useEffect(() => {
    const onSessionExpired = () => {
      if (typeof window !== 'undefined' && isPublicPage(window.location.pathname)) {
        // SECURITY: Clean up auth state even on public pages.
        // Previously this returned early without any cleanup, leaving stale
        // tokens in cookies/localStorage. That caused:
        // 1. The Header to keep rendering authenticated UI and making API calls
        // 2. The middleware to treat the user as "authenticated" (cookie presence check)
        // 3. Token refresh to silently revive the expired session when navigating
        //    to a protected page
        // Now we clear all auth state so isAuthenticated() returns false, the
        // Header switches to the unauthenticated view, and the middleware
        // redirects to login on the next protected-page navigation.
        console.log('[useSessionManager] Session expired on public page — cleaning up auth state (no redirect)');
        setPortalSessionLocked(false);
        setSessionState(prev => ({
          ...prev,
          isActive: false,
          isLocked: false,
          showWarning: false,
        }));
        try {
          authService.clearAuth();
        } catch (error) {
          console.error('[useSessionManager] Error clearing auth on public page:', error);
        }
        return;
      }
      console.log('[useSessionManager] Received sessionExpired event — redirecting to login');
      void handleSessionExpired();
    };
    authService.addEventListener('sessionExpired', onSessionExpired);
    return () => authService.removeEventListener('sessionExpired', onSessionExpired);
  }, [handleSessionExpired]);

  return {
    sessionState,
    extendSession,
    flushOfflinePermissions,
    lockSession,
    unlockSession,
    logout,
    config: finalConfig,
  };
}
